import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import {
  Box,
  Checkbox,
  fade,
  FormControlLabel,
  makeStyles,
} from '@material-ui/core';
import showServerError from '../../../../../helpers/showError';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  TABLE_KEYS,
  updateCanShowShimmer,
} from '../../../../../redux_store';
import { capitalizeFirstLetter } from '../../../../../helpers/dateHelper';
import NoteAddOutlinedIcon from '@material-ui/icons/NoteAddOutlined';
import RoundedButton from '../../../../Shared/RoundedButton';
import MenuItemWithDialog
  from '../../../../Shared/Table/widgets/TopBar/MenuItem/MenuItemWithDialog';
import { getEndpoint } from '../../../../../helpers/endpoint';
import MetricsButton from '../../../../Shared/Table/widgets/MetricsButton';
import DownloadTableFile from '../../../../Shared/Table/widgets/TopBar/DownloadTableFile';
import { getFormattedDate } from '../../../../../helpers/localize';
import { useParams } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  selectorMenu: {
    padding: 3,
    fontWeight: 400,
    maxWidth: 220,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    fontSize: '1rem',
    boxSizing: 'border-box',
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  icon: {
    width: 24,
    height: 24,
    color: '#acb5bd',
  },
  driver: {
    borderTop: `1px solid ${theme.palette.text.secondary}`,
    opacity: 0.2,
  },
  description: {
    maxWidth: 200,
    paddingBottom: 12,
  },
  option: {
    justifyContent: 'flex-start',
  },
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 3, 3, 3),
  },
  btn: {
    marginLeft: theme.spacing(2),
  },
  title: {
    fontWeight: 500,
    paddingTop: theme.spacing(2),
  },
  subtitle: {
    fontSize: '1rem',
  },
  checkbox: {
    marginBottom: 16,
  },
  descriptionContainer: {
    borderTop: `1px solid ${fade('#000000', 0.1)}`,
    marginTop: 24,
    marginBottom: 32,
    fontSize: '1rem',
  },
  descriptionRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5em',
    padding: 16,
    paddingLeft: 0,
    alignItems: 'baseline',
    borderBottom: `1px solid ${fade('#000000', 0.1)}`,
  },
  descriptionRowName: {
    flex: 1,
    textAlign: 'end',
    color: '#888888',
    width: 260,
    minWidth: 260,
  },
  descriptionRowValue: {
    flex: 1,
    fontWeight: '500',
  },
}));

const TableActions = (props) => {
  const {
    totalCount,
    totalAccountsCount,
    accountUuid,
    accountBusinessName,
    reloadData,
    filter,
    sort,
    aggregations,
  } = props;
  const { en } = useSelector((state) => state.i18n);
  const { tables, errors, btn, dialogs } = en;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [downloadXlsxFile, toggleDownloadXlsxFile] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isOpenStatusDialog, setOpenStatusDialog] = useState(false);
  const { accountUuid: paramAccountUuid } = useParams();
  const tableKey = paramAccountUuid ? TABLE_KEYS.reportableDSTable : TABLE_KEYS.reportableTable;

  const getActionList = (newEndpoint, patch) => ({
    method: 'POST',
    endpoint: newEndpoint,
    credentials: 'include',
    body: patch,
    responseType: 'blob',
  });

  const { mutate: getActions, loading: isStatusLoading } =
    useMutation(getActionList);

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
  };

  const downloadResultExcelFile = (payload, _fileName) => {
    const url = window.URL.createObjectURL(
      new Blob([payload], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
      }),
    );
    const link = document.createElement('a');
    link.href = url;
    const today = new Date();
    const options = {
      month: 'long',
    };
    const month = capitalizeFirstLetter(
      today.toLocaleString('en-GB', options),
    ).slice(0, 3);
    const fileName = _fileName ?? `${accountBusinessName} ISP Services ${month} ${today.getFullYear()}.xlsx`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleChangeSelectedStatus = async () => {
    const data = {
      statusAction: 'reported',
      totalCount: totalCount,
    };
    let newEndpoint = getEndpoint(`/accounts/${accountUuid}/reportables/actions`, {}, sort, filter);

    const { payload, error, status, headers } = await getActions(
      newEndpoint,
      data,
    );
    if (!error) {
      let fileName;

      if (headers.has('Attachment-Name')) {
        fileName = headers.get('Attachment-Name').toString();
      }

      if (payload && payload.size > 0 && downloadXlsxFile) {
        await downloadResultExcelFile(payload, fileName);
      }
      dispatch(
        enqueueSnackbar({
          message: dialogs.reportHasBeenGenerated,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      setIsSuccess(true);
      dispatch(updateCanShowShimmer(true));
      setTimeout(() => {
        reloadData(true);
        handleCloseStatusDialog();
      }, 200);

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } else {
      switch (status) {
        case 422:
        case 500: {
          const obj = JSON.parse(await payload.text());
          if (obj.validationErrors) {
            Object.keys(obj.validationErrors).forEach((key) =>
              showServerError(dispatch, obj.validationErrors[key]),
            );
            break;
          }
          showServerError(dispatch, errors.validationError);
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
      handleCloseStatusDialog();
      setIsFailed(true);

      setTimeout(() => {
        setIsFailed(false);
      }, 3000);
    }
  };

  return <>
    <MetricsButton title={'reportables'} aggregations={aggregations} filter={filter} />
    <MenuItemWithDialog
      icon={<NoteAddOutlinedIcon className={classes.icon} />}
      label={tables.generateReport}
      isDisabled={!totalCount}
      onClick={() => {
        if ((totalAccountsCount ?? 0) > 1) {
          dispatch(
            enqueueSnackbar({
              message: dialogs.canUseForSeveralAccounts,
              options: {
                variant: SNACKBAR_VARIANTS.warning,
              },
            }),
          );
        } else {
          setOpenStatusDialog(true);
        }
      }}
      isLoading={isStatusLoading}
      isSuccess={isSuccess}
      isFailed={isFailed}
      dialogTitle={dialogs.generateClaimsReport}
      dialogSubtitle={
        <>{dialogs.generateClaimsReportDescription1}
          <Box fontWeight="600" display="inline">
            {dialogs.generateClaimsReportDescription2}
          </Box>
          {dialogs.generateClaimsReportDescription3}</>}
      dialogIsOpen={isOpenStatusDialog}
      dialogHandleClose={handleCloseStatusDialog}
      dialogOptions={[
        {
          label: tables.accountName,
          value: accountBusinessName,
        },
        {
          label: tables.numberOfClaimCPTLineItems,
          value: totalCount,
        },
      ]}
      dialogAdditionalOptions={
        <FormControlLabel
          disabled={isStatusLoading}
          control={
            <Checkbox
              name={'downloadXlsxFile'}
              checked={downloadXlsxFile}
              disabled={isStatusLoading}
              color="primary"
              onChange={() => {
                toggleDownloadXlsxFile(!downloadXlsxFile);
              }}
            />
          }
          className={classes.checkbox}
          label={dialogs.downloadXlsxFile}
        />}
      dialogActions={<>
        <RoundedButton
          onClick={handleChangeSelectedStatus}
          isLoading={isStatusLoading}
          disabled={isStatusLoading}
          variant="contained"
          color="primary"
          size="small"
          className={classes.btn}
          LoadingWrapperProps={{
            LoaderProps: {
              disableShrink: true,
            },
          }}
        >
          {btn.verifyAndGenerate}
        </RoundedButton>
      </>}
    />
    <DownloadTableFile
      fileName={`Reportables ${getFormattedDate(new Date(Date.now()))}.csv`}
      endpoint={'/export-reportables.csv'}
      tableKey={tableKey}
      tableFilter={filter}
      tableSort={sort}
      totalCount={totalCount}
    />
  </>;
};

export default TableActions;
